// Purpose: /shares — list of sender's shares with multi-select bulk
// revoke. Phase 7.5 port: PageHeader + FilterBar (search + status) +
// DataTable. Bulk-selection toolbar slides above the table when any
// rows are checked.
// Commit 8b.16: replaced the status dropdown with an Active/Archive/All
// segment control. Default = Active (with prominent filter chip so the
// shift isn't a silent surprise on refresh). Per-share row shows a
// recipient summary ("3 recipients · 2 downloaded · last activity 2h
// ago"). Archive view rows render muted. URL `?filter=` persists across
// refresh.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  bulkArchiveShares,
  bulkRevokeShares,
  listShares,
  type BulkRevokeOffender,
  type ShareListFilter,
  type ShareSummary,
} from '../api/shares.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { PlusIcon } from '../components/icons/index.tsx';
import { PageHeader } from '../components/nav/PageHeader.tsx';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  FilterBar,
  Modal,
  SearchInput,
  type Column,
} from '../components/ui/index.ts';

function formatBytes(n: string): string {
  const bytes = Number(n);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// 8b.16: short relative-time stamp for the per-share activity summary.
function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function shareBadge(s: ShareSummary) {
  if (s.status === 'REVOKED') {
    return <Badge tone="danger">Revoked</Badge>;
  }
  if (s.status === 'EXPIRED') {
    return <Badge tone="neutral">Expired</Badge>;
  }
  if (s.consumedViaZip) {
    return <Badge tone="success">Downloaded</Badge>;
  }
  if (s.locked) {
    return <Badge tone="warning">Locked</Badge>;
  }
  if (s.status === 'LIVE') {
    return <Badge tone="info">Live</Badge>;
  }
  return <Badge tone="neutral">{s.status}</Badge>;
}

function isFilter(v: string | null): v is ShareListFilter {
  return v === 'active' || v === 'archive' || v === 'all';
}

export function Shares() {
  const { status } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shares, setShares] = useState<ShareSummary[] | null>(null);
  const [counts, setCounts] = useState<{ active: number; archive: number; all: number }>({
    active: 0,
    archive: 0,
    all: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [offenders, setOffenders] = useState<BulkRevokeOffender[] | null>(null);
  const [query, setQuery] = useState('');
  // 8b.16.1: ephemeral status banner (toast-style) for bulk-action
  // results. Shows "3 archived" or "3 of 5 archived. 2 already revoked."
  // Cleared on selection change or by user dismissing.
  const [status_msg, setStatusMsg] = useState<{
    tone: 'success' | 'warning';
    text: string;
  } | null>(null);
  // 8b.16: filter is the primary list-view dimension. Default Active;
  // Archive shows isArchived=true; All shows everything (less common,
  // kept as escape hatch).
  const filterParam = searchParams.get('filter');
  const filter: ShareListFilter = isFilter(filterParam) ? filterParam : 'active';

  const reload = async (current: ShareListFilter): Promise<void> => {
    try {
      const fresh = await listShares(current);
      setShares(fresh.shares);
      setCounts(fresh.counts);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  useEffect(() => {
    if (status !== 'authenticated') return;
    void reload(filter);
  }, [status, filter]);

  function setFilter(next: ShareListFilter) {
    if (next === 'active') searchParams.delete('filter');
    else searchParams.set('filter', next);
    setSearchParams(searchParams, { replace: true });
    setSelected(new Set());
    setOffenders(null);
    setStatusMsg(null);
  }

  const filtered = useMemo(() => {
    if (!shares) return null;
    const q = query.trim().toLowerCase();
    if (!q) return shares;
    return shares.filter((s) => (s.name ?? '').toLowerCase().includes(q) || s.id.includes(q));
  }, [shares, query]);

  const offenderMap: Record<string, BulkRevokeOffender['reason']> = {};
  if (offenders) {
    for (const o of offenders) offenderMap[o.id] = o.reason;
  }

  const toggleOne = (id: string, checked: boolean): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
    setOffenders(null);
    setStatusMsg(null);
  };

  // 8b.16.1: select-all toggles every share in the CURRENT filtered view.
  // Selecting "all" across pages would be confusing — we don't paginate
  // today, but the contract is clear: header checkbox = "what's visible".
  const toggleAllVisible = (checked: boolean, visible: ShareSummary[]): void => {
    setOffenders(null);
    setStatusMsg(null);
    if (!checked) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(visible.map((s) => s.id)));
  };

  const runBulkRevoke = async (): Promise<void> => {
    setWorking(true);
    setError(null);
    try {
      const result = await bulkRevokeShares(Array.from(selected));
      if (result.ok) {
        const n = selected.size;
        setConfirmOpen(false);
        setSelected(new Set());
        setOffenders(null);
        setStatusMsg({ tone: 'success', text: `${n} share${n === 1 ? '' : 's'} revoked.` });
        await reload(filter);
      } else {
        setOffenders(result.offenders);
        setStatusMsg({
          tone: 'warning',
          text: `Cannot revoke ${result.offenders.length} of ${selected.size}: ${summarizeOffenders(result.offenders)}. Adjust selection and retry.`,
        });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  // 8b.16.1: bulk archive. No confirm modal — low-stakes, undoable
  // (Unarchive button on detail page restores). Server returns
  // {flipped: N} on success or {offenders: [...]} on validation failure
  // (e.g. share not owned by caller — atypical via UI but possible if
  // selection drifted across a logout/login boundary).
  const runBulkArchive = async (): Promise<void> => {
    if (selected.size === 0) return;
    setWorking(true);
    setError(null);
    setStatusMsg(null);
    try {
      const ids = Array.from(selected);
      const result = await bulkArchiveShares(ids, true);
      if ('offenders' in result) {
        setOffenders(result.offenders);
        setStatusMsg({
          tone: 'warning',
          text: `Cannot archive ${result.offenders.length} of ${ids.length}: ${summarizeOffenders(result.offenders)}.`,
        });
      } else {
        const flipped = result.flipped;
        const skipped = ids.length - flipped;
        const text =
          skipped > 0
            ? `${flipped} of ${ids.length} archived (${skipped} already archived).`
            : `${flipped} share${flipped === 1 ? '' : 's'} archived.`;
        setSelected(new Set());
        setOffenders(null);
        setStatusMsg({ tone: 'success', text });
        await reload(filter);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  if (status === 'loading') {
    return <p className="text-body-m text-fg-muted">Loading…</p>;
  }
  if (status === 'unauthenticated') {
    return (
      <p className="text-body-m text-fg-secondary">
        Please{' '}
        <Link to="/login" className="text-accent-primary underline">
          sign in
        </Link>{' '}
        to see your shares.
      </p>
    );
  }

  // 8b.16: archive rows render muted to signal "reference only — not the
  // common path". Active rows keep the regular contrast.
  const rowClass = (s: ShareSummary): string => (s.isArchived ? 'opacity-60' : '');

  const columns: Column<ShareSummary>[] = [
    {
      key: 'select',
      // 8b.16.1: select-all checkbox in header. Toggles every row in
      // the current filtered view; "all" means "everything visible
      // here", not "everything across pages" (no pagination today).
      header:
        filtered && filtered.length > 0 ? (
          <input
            type="checkbox"
            checked={filtered.length > 0 && filtered.every((s) => selected.has(s.id))}
            ref={(el) => {
              if (el) {
                el.indeterminate = selected.size > 0 && !filtered.every((s) => selected.has(s.id));
              }
            }}
            onChange={(e) => toggleAllVisible(e.target.checked, filtered)}
            aria-label="Select all visible"
            data-testid="select-all"
          />
        ) : (
          <span className="sr-only">Select</span>
        ),
      width: '40px',
      cell: (s) => (
        <input
          type="checkbox"
          checked={selected.has(s.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => toggleOne(s.id, e.target.checked)}
          aria-label={`Select ${s.name ?? s.id}`}
        />
      ),
    },
    {
      key: 'name',
      header: 'Name',
      cell: (s) => {
        const reason = offenderMap[s.id];
        // 8b.16: per-share recipient summary line. Hidden when there are
        // no recipients (pre-8b.14 unbackfillable shares).
        const summary =
          s.recipientCount > 0
            ? `${s.recipientCount} recipient${s.recipientCount === 1 ? '' : 's'} · ${s.accessedCount} downloaded${
                s.lastActivityAt ? ` · last activity ${timeAgo(s.lastActivityAt)}` : ''
              }`
            : null;
        return (
          <div className={`flex flex-col gap-1 min-w-0 ${rowClass(s)}`}>
            <span className="font-semibold text-fg-primary truncate">{s.name ?? '(unnamed)'}</span>
            {summary ? <span className="text-caption text-fg-muted">{summary}</span> : null}
            {reason ? (
              <span className="text-body-s text-status-danger">Cannot revoke: {reason}</span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'files',
      header: 'Files',
      width: '100px',
      hideOnMobile: true,
      cell: (s) => (
        <span className={`text-body-s text-fg-secondary ${rowClass(s)}`}>{s.fileCount}</span>
      ),
    },
    {
      key: 'size',
      header: 'Size',
      width: '120px',
      hideOnMobile: true,
      cell: (s) => (
        <span className={`text-body-s text-fg-secondary ${rowClass(s)}`}>
          {formatBytes(s.sizeBytes)}
        </span>
      ),
    },
    {
      key: 'expires',
      header: 'Expires',
      hideOnMobile: true,
      cell: (s) => (
        <span className={`text-body-s text-fg-muted ${rowClass(s)}`}>
          {s.expiresAt ? new Date(s.expiresAt).toLocaleString() : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '140px',
      cell: (s) => <span className={rowClass(s)}>{shareBadge(s)}</span>,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Shares"
        subtitle="One-to-one encrypted transfers. Recipients verify with an out-of-band access code."
        actions={
          <Button
            variant="primary"
            size="md"
            leftIcon={<PlusIcon size={16} strokeWidth={2.5} />}
            onClick={() => navigate('/shares/new')}
          >
            New share
          </Button>
        }
      />

      {/* 8b.16: prominent filter chip so the default-Active shift is
          obvious. Click All to escape; the segment control directly
          below is the persistent filter switch. */}
      <Card padding="compact" className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-body-s text-fg-secondary">
          <span className="font-semibold text-fg-primary">Showing:</span>
          <Badge tone={filter === 'archive' ? 'neutral' : 'info'}>
            {filter === 'active' ? 'Active' : filter === 'archive' ? 'Archive' : 'All'}
          </Badge>
          <span className="text-fg-muted">
            ·{' '}
            {filter === 'active'
              ? counts.active
              : filter === 'archive'
                ? counts.archive
                : counts.all}
          </span>
        </div>
        {filter !== 'all' ? (
          <Button variant="subtle" size="sm" onClick={() => setFilter('all')}>
            Show everything
          </Button>
        ) : null}
      </Card>

      <FilterBar>
        <SearchInput
          placeholder="Search by name or ID"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="inline-flex rounded-md border border-border-default overflow-hidden">
          <FilterButton
            current={filter}
            value="active"
            label={`Active (${counts.active})`}
            onClick={setFilter}
          />
          <FilterButton
            current={filter}
            value="archive"
            label={`Archive (${counts.archive})`}
            onClick={setFilter}
          />
          <FilterButton
            current={filter}
            value="all"
            label={`All (${counts.all})`}
            onClick={setFilter}
          />
        </div>
      </FilterBar>

      {selected.size > 0 ? (
        <Card padding="compact" className="flex items-center justify-between gap-4">
          <span className="text-body-m text-fg-primary">
            {selected.size} selected
            {offenders ? (
              <Badge tone="danger" className="ml-3">
                {offenders.length} cannot be acted on
              </Badge>
            ) : null}
          </span>
          <div className="flex gap-2">
            {/* 8b.16.1: Archive button. No confirm — low-stakes, undoable
                via Unarchive on the detail page. */}
            <Button
              variant="secondary"
              size="sm"
              loading={working}
              onClick={() => void runBulkArchive()}
              data-testid="bulk-archive"
            >
              Archive
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={working}
              onClick={() => setConfirmOpen(true)}
              data-testid="bulk-revoke"
            >
              Revoke
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={working}
              onClick={() => {
                setSelected(new Set());
                setOffenders(null);
                setStatusMsg(null);
              }}
            >
              Clear
            </Button>
          </div>
        </Card>
      ) : null}

      {status_msg ? (
        <div
          role="status"
          className={`px-4 py-3 rounded-md text-body-s border flex items-center justify-between gap-3 ${
            status_msg.tone === 'success'
              ? 'border-status-success/30 bg-status-success-muted text-status-success'
              : 'border-status-warning/30 bg-status-warning-muted text-status-warning'
          }`}
        >
          <span>{status_msg.text}</span>
          <button
            type="button"
            className="text-fg-muted hover:text-fg-primary"
            aria-label="Dismiss"
            onClick={() => setStatusMsg(null)}
          >
            ×
          </button>
        </div>
      ) : null}

      {error ? (
        <div
          role="alert"
          className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
        >
          {error}
        </div>
      ) : null}

      {filtered === null ? (
        <p className="text-body-m text-fg-muted">Loading shares…</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={filter === 'archive' ? 'No archived shares' : 'No shares yet'}
          description={
            filter === 'archive'
              ? 'Shares you archive (or that auto-archive after 7 days in a terminal state) will show up here.'
              : shares && shares.length === 0
                ? 'Click "New share" to create one.'
                : 'No shares match the current search.'
          }
          action={
            shares && shares.length === 0 && filter !== 'archive' ? (
              <Button
                variant="primary"
                leftIcon={<PlusIcon size={16} strokeWidth={2.5} />}
                onClick={() => navigate('/shares/new')}
              >
                New share
              </Button>
            ) : undefined
          }
        />
      ) : (
        <DataTable<ShareSummary>
          columns={columns}
          rows={filtered}
          getRowKey={(s) => s.id}
          onRowClick={(s) => navigate(`/shares/${s.id}`)}
          caption="Shares"
        />
      )}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={`Revoke ${selected.size} share${selected.size === 1 ? '' : 's'}`}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={working}
              onClick={() => void runBulkRevoke()}
            >
              Revoke all
            </Button>
          </>
        }
      >
        <p>
          This is atomic — if any of the selected shares is not revocable (not found, already
          revoked, or not LIVE) the entire batch rolls back and those rows are highlighted so you
          can retry the clean subset.
        </p>
      </Modal>
    </div>
  );
}

// 8b.16.1: human-readable summary of an offender list for the toast.
// Counts by reason ("2 already revoked, 1 not LIVE") so the user knows
// why each row was rejected without having to expand the offender table.
function summarizeOffenders(offenders: BulkRevokeOffender[]): string {
  const labels: Record<string, string> = {
    not_found: 'not found',
    already_revoked: 'already revoked',
    not_live: 'not LIVE',
    not_owner: 'not owned',
  };
  const counts = new Map<string, number>();
  for (const o of offenders) {
    const key = o.reason as string;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const parts: string[] = [];
  for (const [key, n] of counts.entries()) {
    parts.push(`${n} ${labels[key] ?? key}`);
  }
  return parts.join(', ');
}

function FilterButton({
  current,
  value,
  label,
  onClick,
}: {
  current: ShareListFilter;
  value: ShareListFilter;
  label: string;
  onClick: (next: ShareListFilter) => void;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      data-testid={`filter-${value}`}
      className={`px-3 py-1.5 text-body-s ${
        active
          ? 'bg-accent-primary text-white'
          : 'bg-surface-elevated text-fg-secondary hover:bg-bg-tertiary'
      }`}
    >
      {label}
    </button>
  );
}
